import {
  CalendarBlank,
  CaretDown,
  CirclesThreePlus,
  Copy,
  Gear,
  House,
  List,
  MagnifyingGlass,
  PlugsConnected,
  Robot,
  ShieldCheck,
  SignOut,
  SlidersHorizontal,
  Stack,
  TrayArrowDown,
  UserFocus,
  X,
  Gavel,
  Flask,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { pathWithLocale, useI18n, type Locale } from "../i18n";
import { Brand } from "./Brand";
import { LanguageSwitch } from "./LanguageSwitch";
import { ProductGuide } from "./ProductGuide";

interface NavigationItem {
  key: Parameters<ReturnType<typeof useI18n>["t"]>[0];
  to: string;
  icon: ComponentType<{ size?: number; weight?: "regular" | "fill"; "aria-hidden"?: boolean }>;
  adminOnly?: boolean;
  allowedRoles?: string[];
}

const NAVIGATION: Array<{
  labelKey: Parameters<ReturnType<typeof useI18n>["t"]>[0];
  items: NavigationItem[];
}> = [
  {
    labelKey: "nav.group.supply",
    items: [
      { key: "nav.overview", to: "/app", icon: House },
      { key: "nav.connections", to: "/app/connections", icon: PlugsConnected },
      { key: "nav.ingestion", to: "/app/ingestion", icon: TrayArrowDown },
    ],
  },
  {
    labelKey: "nav.group.govern",
    items: [
      { key: "nav.knowledge", to: "/app/knowledge", icon: Stack },
      { key: "nav.duplicates", to: "/app/duplicates", icon: Copy, allowedRoles: ["OWNER", "ADMIN", "REVIEWER"] },
      { key: "nav.trust", to: "/app/trust", icon: ShieldCheck },
      { key: "nav.reviews", to: "/app/reviews", icon: UserFocus, allowedRoles: ["OWNER", "ADMIN", "REVIEWER"] },
      { key: "nav.adjudication", to: "/app/adjudication", icon: Gavel, allowedRoles: ["OWNER", "ADMIN", "REVIEWER"] },
      { key: "nav.calibration", to: "/app/calibration", icon: Flask, allowedRoles: ["OWNER", "ADMIN", "REVIEWER"] },
    ],
  },
  {
    labelKey: "nav.group.activate",
    items: [
      { key: "nav.search", to: "/app/search", icon: MagnifyingGlass },
      { key: "nav.agents", to: "/app/agents", icon: Robot },
    ],
  },
  {
    labelKey: "nav.group.operate",
    items: [
      {
        adminOnly: true,
        key: "nav.platform",
        to: "/app/platform",
        icon: SlidersHorizontal,
      },
      { key: "nav.settings", to: "/app/settings", icon: Gear },
    ],
  },
];

const MOBILE_NAVIGATION_QUERY = "(max-width: 768px)";
function workspacePlanLabel(plan: string | undefined, locale: Locale): string {
  if (locale === "en") return `${plan?.toLowerCase() ?? "standard"} workspace`;
  if (plan === "ENTERPRISE") return "企业工作区";
  if (plan === "TRIAL") return "试用工作区";
  return "工作区";
}

function mobileNavigationMatches(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(MOBILE_NAVIGATION_QUERY).matches
  );
}

export function AppShell() {
  const { locale, t } = useI18n();
  const auth = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavigation, setMobileNavigation] = useState(
    mobileNavigationMatches,
  );
  const date = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === "zh" ? "zh-CN" : "en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date()),
    [locale],
  );

  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(MOBILE_NAVIGATION_QUERY);
    const update = () => setMobileNavigation(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const navigationHidden = mobileNavigation && !menuOpen;
  const planLabel = workspacePlanLabel(auth.workspace?.plan, locale);
  const activeItem = NAVIGATION.flatMap((group) => group.items)
    .filter((item) => item.to === "/app" ? location.pathname === item.to : location.pathname.startsWith(item.to))
    .sort((left, right) => right.to.length - left.to.length)[0];

  return (
    <div className="app-shell">
      <aside
        aria-hidden={navigationHidden || undefined}
        className={`app-sidebar${menuOpen ? " is-open" : ""}`}
        id="app-navigation-sidebar"
        inert={navigationHidden || undefined}
      >
        <div className="app-sidebar__brand">
          <Brand />
          <button
            aria-label={locale === "zh" ? "关闭导航" : "Close navigation"}
            className="icon-button app-sidebar__close"
            onClick={() => setMenuOpen(false)}
            type="button"
          >
            <X aria-hidden="true" size={20} />
          </button>
        </div>
        <button className="workspace-switcher" type="button">
          <span className="workspace-switcher__avatar">
            {auth.workspace?.name.slice(0, 1).toUpperCase()}
          </span>
          <span>
            <strong>{auth.workspace?.name}</strong>
            <small>{planLabel}</small>
          </span>
          <CaretDown aria-hidden="true" size={15} />
        </button>
        <div className="experience-switcher">
          <span>{locale === "zh" ? "当前体验" : "Current experience"}</span>
          <NavLink to={pathWithLocale("/work", locale)}>
            <CirclesThreePlus aria-hidden="true" size={16} />
            <strong>{locale === "zh" ? "治理平台" : "Governance platform"}</strong>
            <span>{locale === "zh" ? "切换到业务工作台" : "Switch to workbench"}</span>
          </NavLink>
        </div>
        <nav aria-label={locale === "zh" ? "产品导航" : "Product navigation"} className="app-navigation">
          {NAVIGATION.map((group) => (
            <div className="app-navigation__group" key={group.labelKey}>
              <p>{t(group.labelKey)}</p>
              {group.items.map((item) => {
                if (item.adminOnly && !auth.user?.isPlatformAdmin) return null;
                if (item.allowedRoles && !item.allowedRoles.includes(auth.workspace?.role ?? "")) return null;
                const Icon = item.icon;
                return (
                  <NavLink
                    className={({ isActive }) =>
                      `app-navigation__item${isActive ? " is-active" : ""}`
                    }
                    end={item.to === "/app"}
                    key={item.to}
                    to={pathWithLocale(item.to, locale)}
                  >
                    <Icon aria-hidden={true} size={17} weight="regular" />
                    <span>{t(item.key)}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="app-sidebar__profile">
          <span className="profile-avatar">{auth.user?.name.slice(0, 1).toUpperCase()}</span>
          <span>
            <strong>{auth.user?.name}</strong>
            <small>{auth.user?.email}</small>
          </span>
          <button
            aria-label={t("nav.signOut")}
            className="icon-button"
            onClick={() => void auth.logout()}
            title={t("nav.signOut")}
            type="button"
          >
            <SignOut aria-hidden="true" size={18} />
          </button>
        </div>
      </aside>
      {menuOpen ? (
        <button
          aria-hidden="true"
          aria-label={locale === "zh" ? "关闭导航" : "Close navigation"}
          className="sidebar-scrim"
          onClick={() => setMenuOpen(false)}
          tabIndex={-1}
          type="button"
        />
      ) : null}
      <div className="app-frame">
        <header className="app-topbar">
          <button
            aria-controls="app-navigation-sidebar"
            aria-expanded={menuOpen}
            aria-label={locale === "zh" ? "打开导航" : "Open navigation"}
            className="icon-button app-topbar__menu"
            onClick={() => setMenuOpen(true)}
            type="button"
          >
            <List aria-hidden="true" size={21} />
          </button>
          <div className="app-topbar__context">
            <span>{locale === "zh" ? "可信知识控制台" : "Trusted knowledge control plane"}</span>
            <strong>{activeItem ? t(activeItem.key) : t("nav.overview")}</strong>
          </div>
          <div className="app-topbar__spacer" />
          <span className="app-topbar__date">
            <CalendarBlank aria-hidden="true" size={16} />
            {date}
          </span>
          <ProductGuide experience="governance" locale={locale} />
          <LanguageSwitch minimal />
          <span className="app-topbar__user">{auth.user?.name}</span>
        </header>
        <main className="app-content" id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
